import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { HelpCircle } from "lucide-react";

const STAGE_DEFINITIONS = [
  {
    value: "new",
    label: "New (Unreviewed)",
    badgeClass: "border-gray-300 text-gray-600 bg-gray-50",
    dotColor: "bg-gray-400",
    definition:
      "Accounts which have been added but not dispositioned by the SE. Previously disqualified accounts that have been identified as new potential due to some change will also reappear here.",
    entry: "Bulk import, enrichment sync, AI-identified potential targets, or re-flagged from Disqualified",
    next: "SE reviews and moves to Cold Target, or disqualifies with reason",
    outreach: "No — not yet approved for outreach",
  },
  {
    value: "cold_target",
    label: "Cold Target",
    badgeClass: "border-blue-300 text-blue-600 bg-blue-50",
    dotColor: "bg-blue-500",
    definition:
      "Accounts we want to be doing outreach toward. They stay cold until a certain level of conversation has occurred — back-and-forth exchange on LinkedIn, positive or disqualifying response to an email, or a request to be contacted from the website.",
    entry: "SE manually moves from New. ICP score helps identify good candidates in the New bucket.",
    next: "Auto-promoted to Warm Target when engagement signals fire (LinkedIn connection accept, email reply, interested flag)",
    outreach: "Yes — eligible for cold outreach campaigns",
    note: "Lead score does NOT move someone from Cold to Warm. Only real engagement signals do.",
  },
  {
    value: "warm_target",
    label: "Warm Target",
    badgeClass: "border-amber-400 text-amber-700 bg-amber-50",
    dotColor: "bg-amber-500",
    definition:
      "Accounts where we have engaged in an open dialogue with one or more contacts. Not yet an opportunity. Automatically moved to warm when discussions are active per HeyReach and email activity.",
    entry: "Auto-promoted from Cold Target on engagement signal, or manually set by SE",
    next: "SE manually promotes to Active Prospect when opportunity-level discussions begin",
    outreach: "Yes — but tailored differently. Outreach should acknowledge existing relationship (e.g., \"we've been in touch with your colleague\")",
  },
  {
    value: "active_prospect",
    label: "Active Prospect",
    badgeClass: "border-green-400 text-green-700 bg-green-50",
    dotColor: "bg-green-500",
    definition:
      "Accounts that are not yet a customer (no equipment sales) but have moved from a warm target into the active opportunity phase. This is a manual shift from Warm to Active Prospect by the SE based on amount of activity and discussion.",
    entry: "SE manually promotes from Warm Target",
    next: "Close → Customer, or stall → back to Warm/Cold",
    outreach: "SE-directed only — platform stops broadly reaching out. SE can choose specific contacts to go after with a specific goal.",
  },
  {
    value: "customer",
    label: "Customer",
    badgeClass: "border-emerald-400 text-emerald-700 bg-emerald-50",
    dotColor: "bg-emerald-500",
    definition:
      "An account that we have already made a sale to. Similar to Active Prospect, we will only outreach to these customers if directed by SE.",
    entry: "Deal closed-won",
    next: "Ongoing relationship management",
    outreach: "SE-directed only",
    note: "For multi-site customers: parent/child account hierarchy determines whether different sites are treated as discrete or linked for relationship leverage.",
  },
  {
    value: "disqualified",
    label: "Disqualified",
    badgeClass: "border-red-300 text-red-600 bg-red-50",
    dotColor: "bg-red-500",
    definition:
      "Accounts that have no potential for sale. A disqualification reason is required.",
    entry: "SE disqualifies with required reason",
    next: "Once a month, AI checks to see if anything has changed that would cause us to re-review. If yes, reverts to New with specific info about what changed.",
    outreach: "No — permanently blocked from campaigns",
  },
];

export function TargetUniverseStagesDialog() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground hover:text-foreground">
          <HelpCircle className="h-4 w-4" />
          <span className="text-xs">Stages Explained</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Target Universe — Account Stages</DialogTitle>
          <p className="text-sm text-muted-foreground">
            How accounts move through the sales lifecycle. Defined by the CARR sales team.
          </p>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {STAGE_DEFINITIONS.map((stage) => (
            <div
              key={stage.value}
              className="border rounded-lg p-4 space-y-2"
            >
              <div className="flex items-center gap-2">
                <span className={`h-2.5 w-2.5 rounded-full ${stage.dotColor}`} />
                <Badge variant="outline" className={`text-xs font-medium ${stage.badgeClass}`}>
                  {stage.label}
                </Badge>
              </div>

              <p className="text-sm text-foreground leading-relaxed">
                {stage.definition}
              </p>

              <div className="grid grid-cols-1 gap-1.5 text-xs text-muted-foreground">
                <div>
                  <span className="font-semibold text-foreground">How accounts get here: </span>
                  {stage.entry}
                </div>
                <div>
                  <span className="font-semibold text-foreground">What happens next: </span>
                  {stage.next}
                </div>
                <div>
                  <span className="font-semibold text-foreground">Outreach: </span>
                  {stage.outreach}
                </div>
              </div>

              {stage.note && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mt-1">
                  ⚠ {stage.note}
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="mt-4 p-3 bg-muted/50 rounded-lg">
          <p className="text-xs text-muted-foreground">
            <span className="font-semibold">Note:</span> ICP Tier (1/2/3) is separate from Account Status.
            ICP measures <em>how well they match our profile</em>. Account Status measures{" "}
            <em>where they are in our outreach lifecycle</em>.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
